import React, { useEffect, useState } from 'react';
import { GestureResponderEvent, Modal, Platform, Pressable, ScrollView, View, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAppSelector, useAppDispatch } from '../../../hooks/store-hook';
import useCustomTheme from '../../../hooks/theme-hook';
import useTranslation from '../../../hooks/translation-hook';
import Overlay from '../Overlay';
import { lightBoxActions } from '../../../store/lightBoxSlice';
import Loading from '../Loading';
import { StylesHelper } from '../../../models/helpers/stylesHelper';
import Animated, { Easing, interpolate, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';

const LightBox = () => {
  const t = useTranslation();
  const theme = useCustomTheme();
  const layout = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const lightBoxStore = useAppSelector((selector) => selector.lightBox);
  const dispatch = useAppDispatch();
  const style = useLightBoxStyles();
  const nativeBottomInset = Platform.OS === 'android' ? Math.max(insets.bottom, 0) : 0;

  const [isLoading, setIsLoading] = useState(false);
  const progress = useSharedValue(0);
  const secondProgress = useSharedValue(0);

  useEffect(() => {
    setIsLoading(lightBoxStore.isLoading ?? false);
  }, [lightBoxStore.isLoading]);

  useEffect(() => {
    // console.log('ModalStore', modalStore.visible);
  }, [lightBoxStore.visible]);

  useEffect(() => {
    if (lightBoxStore.visible) {
      progress.value = 0;
      progress.value = withTiming(1, { duration: 220, easing: Easing.out(Easing.quad) });
    } else {
      progress.value = 0;
    }
  }, [lightBoxStore.visible, progress]);

  useEffect(() => {
    if (lightBoxStore.secondVisible) {
      secondProgress.value = 0;
      secondProgress.value = withTiming(1, { duration: 220, easing: Easing.out(Easing.quad) });
    } else {
      secondProgress.value = 0;
    }
  }, [lightBoxStore.secondVisible, secondProgress]);

  // `noDismiss` can be enabled by specific flows (e.g. required forms).
  // Reset it once the lightbox is closed to avoid leaking into the next popup.
  useEffect(() => {
    if (!lightBoxStore.visible && lightBoxStore.noDismiss) {
      dispatch(lightBoxActions.setNoDismiss(false));
    }
  }, [dispatch, lightBoxStore.visible, lightBoxStore.noDismiss]);

  useEffect(() => {
    if (lightBoxStore.visible) {
      return;
    }

    const needsReset =
      lightBoxStore.overlayVisible === false ||
      lightBoxStore.allowBackgroundInteraction === true ||
      lightBoxStore.keepDrawerVisible === true ||
      lightBoxStore.contentVerticalAlign !== 'center' ||
      lightBoxStore.contentHorizontalAlign !== 'center' ||
      (lightBoxStore.contentTopOffset ?? 0) !== 0 ||
      (lightBoxStore.contentBottomOffset ?? 0) !== 0 ||
      (lightBoxStore.contentLeftOffset ?? 0) !== 0;

    if (!needsReset) {
      return;
    }

    dispatch(lightBoxActions.setOverlayVisible(true));
    dispatch(lightBoxActions.setAllowBackgroundInteraction(false));
    dispatch(lightBoxActions.setKeepDrawerVisible(false));
    dispatch(lightBoxActions.setContentVerticalAlign('center'));
    dispatch(lightBoxActions.setContentHorizontalAlign('center'));
    dispatch(lightBoxActions.setContentTopOffset(0));
    dispatch(lightBoxActions.setContentBottomOffset(0));
    dispatch(lightBoxActions.setContentLeftOffset(0));
  }, [
    dispatch,
    lightBoxStore.visible,
    lightBoxStore.overlayVisible,
    lightBoxStore.allowBackgroundInteraction,
    lightBoxStore.keepDrawerVisible,
    lightBoxStore.contentVerticalAlign,
    lightBoxStore.contentHorizontalAlign,
    lightBoxStore.contentTopOffset,
    lightBoxStore.contentBottomOffset,
    lightBoxStore.contentLeftOffset,
  ]);

  const cardStyle = useAnimatedStyle(() => {
    const opacity = interpolate(progress.value, [0, 1], [0, 1]);
    const translateY = interpolate(progress.value, [0, 1], [10, 0]);
    const scale = interpolate(progress.value, [0, 1], [0.98, 1]);

    return {
      opacity,
      transform: [{ translateY }, { scale }],
    } as any;
  });

  const secondCardStyle = useAnimatedStyle(() => {
    const opacity = interpolate(secondProgress.value, [0, 1], [0, 1]);
    const translateY = interpolate(secondProgress.value, [0, 1], [10, 0]);
    const scale = interpolate(secondProgress.value, [0, 1], [0.98, 1]);

    return {
      opacity,
      transform: [{ translateY }, { scale }],
    } as any;
  });

  const handleOnClosePress = (e: GestureResponderEvent) => {
    if (lightBoxStore.isLoading || lightBoxStore.noDismiss) {
      return;
    }

    if (lightBoxStore.onClose) {
      lightBoxStore.onClose();
      return;
    }

    dispatch(lightBoxActions.setVisibility(false));
    dispatch(lightBoxActions.setContent(<></>));
    dispatch(lightBoxActions.setNoDismiss(false));
    dispatch(lightBoxActions.setOverlayVisible(true));
    dispatch(lightBoxActions.setAllowBackgroundInteraction(false));
    dispatch(lightBoxActions.setKeepDrawerVisible(false));
    dispatch(lightBoxActions.setContentVerticalAlign('center'));
    dispatch(lightBoxActions.setContentHorizontalAlign('center'));
    dispatch(lightBoxActions.setContentTopOffset(0));
    dispatch(lightBoxActions.setContentBottomOffset(0));
    dispatch(lightBoxActions.setContentLeftOffset(0));
  };

  const handleOnSecondClosePress = () => {

    dispatch(lightBoxActions.setSecondVisibility(false));
    dispatch(lightBoxActions.setContent(<></>));
    lightBoxStore.onSecondClose && lightBoxStore.onSecondClose();
  };

  const verticalAlign = lightBoxStore.contentVerticalAlign ?? 'center';
  const horizontalAlign = lightBoxStore.contentHorizontalAlign ?? 'center';
  const isTopAligned = verticalAlign === 'top';
  const isBottomAligned = verticalAlign === 'bottom';
  const isLeftAligned = horizontalAlign === 'left';
  const topOffset = lightBoxStore.contentTopOffset ?? 0;
  const bottomOffset = lightBoxStore.contentBottomOffset ?? 0;
  const leftOffset = lightBoxStore.contentLeftOffset ?? 0;
  const usesCustomAlignment = isTopAligned || isBottomAligned || isLeftAligned;
  const contentContainerStyle = usesCustomAlignment
    ? {
        width: '100%',
        height: '100%',
        justifyContent: isTopAligned ? 'flex-start' : isBottomAligned ? 'flex-end' : 'center',
        alignItems: isLeftAligned ? 'flex-start' : 'center',
        paddingTop: isTopAligned ? topOffset : 0,
        paddingBottom: isBottomAligned ? bottomOffset + nativeBottomInset : nativeBottomInset,
        paddingLeft: isLeftAligned ? leftOffset : 0,
      }
    : Platform.OS === 'android'
      ? {
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          paddingBottom: nativeBottomInset,
        }
      : undefined;

  const primaryCard = lightBoxStore.visible ? (
    <Animated.View
      style={[
        {
          borderRadius: 8,
          display: lightBoxStore.visible ? 'flex' : 'none',
          backgroundColor: theme.background.modal,
          overflow: 'hidden',
          zIndex: 4501,
          maxHeight: layout.height - 40 - nativeBottomInset,
        },
        cardStyle,
      ]}
    >
      {isLoading && <Loading style={{ width: '100%', height: '100%', backgroundColor: 'rgba(233, 245, 243, 0.8)', zIndex: 4000 }} />}
      {lightBoxStore.content}
    </Animated.View>
  ) : null;

  const secondCard = lightBoxStore.secondVisible ? (
    <Animated.View
      style={[
        {
          borderRadius: 8,
          display: lightBoxStore.secondVisible ? 'flex' : 'none',
          backgroundColor: theme.background.modal,
          overflow: 'hidden',
          zIndex: 4601,
          maxHeight: layout.height - 40 - nativeBottomInset,
        },
        secondCardStyle,
      ]}
    >
      {isLoading && (
        <Loading style={{ width: '100%', height: '100%', ...StylesHelper.padding(12), backgroundColor: 'rgba(233, 245, 243, 0.8)', zIndex: 4000 }} />
      )}
      {lightBoxStore.secondContent}
    </Animated.View>
  ) : null;

  const renderNativeModalLayer = (
    visible: boolean | undefined,
    onClose: (e?: GestureResponderEvent) => void,
    card: React.ReactNode,
    dimBackground: boolean,
  ) => (
    <Modal visible={!!visible} transparent animationType="fade" onRequestClose={() => onClose()} statusBarTranslucent>
      <View style={{ flex: 1 }}>
        <Pressable
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: dimBackground ? 'rgba(0, 0, 0, 0.45)' : 'transparent',
          }}
          onPress={onClose}
        />
        <View
          pointerEvents="box-none"
          style={[
            {
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
              justifyContent: 'center' as any,
              alignItems: 'center' as any,
            },
            contentContainerStyle as any,
          ] as any}
        >
          {card}
        </View>
      </View>
    </Modal>
  );

  return (
    <>
      {Platform.OS !== 'web' && !(lightBoxStore.allowBackgroundInteraction ?? false) ? (
        renderNativeModalLayer(lightBoxStore.visible, handleOnClosePress, primaryCard, lightBoxStore.overlayVisible ?? true)
      ) : (
        <Overlay
          showOverLay={lightBoxStore.visible}
          zIndex={4500}
          onPress={handleOnClosePress}
          dimBackground={lightBoxStore.overlayVisible ?? true}
          allowBackgroundInteraction={lightBoxStore.allowBackgroundInteraction ?? false}
          contentContainerStyle={contentContainerStyle}
        >
          {primaryCard}
        </Overlay>
      )}
      {Platform.OS !== 'web' ? (
        renderNativeModalLayer(lightBoxStore.secondVisible, handleOnSecondClosePress, secondCard, true)
      ) : (
        <Overlay showOverLay={lightBoxStore.secondVisible} zIndex={4600} onPress={handleOnSecondClosePress}>
          {secondCard}
        </Overlay>
      )}
    </>
  );
};

const useLightBoxStyles = () => {
  const theme = useCustomTheme();

  let lightBox = {
    container: {},
    closeButton: {
      position: 'absolute' as any,
      top: 20,
      right: 20,
      xMark: {
        outline: 'none' as any,
      } as any,
    },
  };

  return lightBox as typeof lightBox;
};

export default LightBox;
